<script setup lang="ts">
import { BLOG_CATEGORIES, blogBasePath, blogPostPath, formatBlogDate, type BlogLocale } from '~/constants/blog'
import type { BlogPostListItem } from '~/types'

/**
 * Blog sidebar, ported from the template's widget column: pill search,
 * categories list, recent posts and a closing call to action. Shared by the
 * listing pages and the article page so the two cannot drift apart.
 *
 * The template's "Popular tags" widget is left out on purpose: there are no
 * tag pages to link to, and a list of dead chips is worse than none.
 */
const props = withDefaults(defineProps<{
  /** Posts for the "Recent posts" widget; the widget hides when empty. */
  recentPosts: BlogPostListItem[]
  /** Category slug of the current listing or article, marked active. */
  category?: string | null
  /** Initial value of the search field. */
  searchQuery?: string
  /**
   * Emit `search` while typing (debounced) as the listing does. Off by
   * default so that on an article page typing does not navigate away
   * mid-word; the reader submits with Enter or the button instead.
   */
  liveSearch?: boolean
  /** Which blog it sits in. Only the English one has categories, so only it lists them. */
  locale?: BlogLocale
}>(), {
  category: null,
  searchQuery: '',
  liveSearch: false,
  locale: 'en',
})

const emit = defineEmits<{ search: [term: string] }>()

const { t } = useI18n()

const localSearch = ref(props.searchQuery)
watch(() => props.searchQuery, value => { localSearch.value = value })

const debouncedSearch = useDebounceFn(() => emit('search', localSearch.value), 500)

const onInput = () => {
  if (props.liveSearch) debouncedSearch()
}

const onSubmit = () => emit('search', localSearch.value)

const formatDate = (dateString: string) => formatBlogDate(dateString, props.locale)

/* The template draws its widgets with a dark hairline, not the light card border. */
const widgetClass = 'rounded-[1.25rem] border border-ink p-[1.875rem] dark:border-white/40'
</script>

<template>
  <aside class="flex flex-col gap-y-8">
    <!-- Search -->
    <form class="relative" role="search" @submit.prevent="onSubmit">
      <label for="blog-sidebar-search" class="sr-only">{{ t('blog.sidebar.search') }}</label>
      <input
        id="blog-sidebar-search"
        v-model="localSearch"
        type="search"
        name="search"
        :placeholder="t('blog.searchPlaceholder')"
        class="field-input pr-16"
        @input="onInput"
      >
      <button
        type="submit"
        class="absolute inset-y-0 right-0 flex items-center pl-6 pr-8 text-lg text-ink hover:text-accent dark:text-white"
        :aria-label="t('blog.sidebar.searchButton')"
      >
        <i class="bi bi-search" aria-hidden="true" />
      </button>
    </form>

    <!--
      Categories are links, not buttons: as @click handlers mutating a ref they
      produced no crawlable category URLs at all.
    -->
    <nav v-if="locale === 'en'" :class="widgetClass" :aria-label="t('blog.sidebar.categories')">
      <span class="display-heading display-heading-4 mb-6 block">{{ t('blog.sidebar.categories') }}</span>
      <ul class="flex list-inside list-disc flex-col gap-y-4 text-base">
        <li>
          <LocaleLink
            to="/blog"
            class="hover:text-ink dark:hover:text-white"
            :class="{ 'font-semibold text-ink dark:text-white': !category }"
          >
            {{ t('blog.allCategories') }}
          </LocaleLink>
        </li>
        <li v-for="(label, key) in BLOG_CATEGORIES" :key="key">
          <LocaleLink
            :to="blogBasePath(key)"
            class="hover:text-ink dark:hover:text-white"
            :class="{ 'font-semibold text-ink dark:text-white': category === key }"
            :aria-current="category === key ? 'page' : undefined"
          >
            {{ label }}
          </LocaleLink>
        </li>
      </ul>
    </nav>

    <!-- Recent posts -->
    <div v-if="recentPosts.length > 0" :class="widgetClass">
      <span class="display-heading display-heading-4 mb-6 block">{{ t('blog.sidebar.recent') }}</span>
      <ul class="flex flex-col gap-y-6">
        <li v-for="post in recentPosts" :key="post.id">
          <LocaleLink :to="blogPostPath(post)" class="group flex items-center gap-4">
            <span
              v-if="post.image_url"
              class="size-[5.625rem] shrink-0 overflow-hidden rounded-[6px] bg-surface dark:bg-gray-800"
            >
              <NuxtImg
                :src="post.image_url"
                :alt="t('blog.featuredImageAlt', { title: post.title })"
                width="90"
                height="90"
                format="webp"
                quality="70"
                loading="lazy"
                class="size-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
            </span>
            <span class="flex-1">
              <time
                :datetime="post.published_at || post.created_at"
                class="mb-2 block !text-xs text-muted dark:text-gray-400"
              >
                {{ formatDate(post.published_at || post.created_at) }}
              </time>
              <span class="line-clamp-2 !text-sm font-bold leading-[1.4] text-body group-hover:text-ink dark:text-gray-200 dark:group-hover:text-white">
                {{ post.title }}
              </span>
            </span>
          </LocaleLink>
        </li>
      </ul>
    </div>

    <!-- Call to action, in place of the template's newsletter box -->
    <div class="panel">
      <span class="display-heading display-heading-4 mb-4 block">{{ t('blog.sidebar.ctaTitle') }}</span>
      <p class="mb-6 text-base">{{ t('blog.sidebar.ctaText') }}</p>
      <SofaxBtn to="/free-cv-builder" :label="t('blog.sidebar.ctaButton')" variant="accent" block />
    </div>
  </aside>
</template>
